import React, { useState, useEffect } from "react";
import { Modal, Input, Select, Collapse, message } from "antd";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

export default function KpiModal({
    open,
    onCancel,
    onSave,
    title,
    activeTab,
    uomTypes,
    loading,
    selectedParentId,
    selectedParentUOMId,
    depts,
    selectedMainDept,
}) {
    const [sessionUserData, setsessionUserData] = useState({});
    const [activeKeys, setActiveKeys] = useState([]);
    const [selectedDept, setSelectedDept] = useState(null);

    useEffect(() => {
        const userDataString = sessionStorage.getItem("userData");
        if (userDataString) {
            const userData = JSON.parse(userDataString);
            setsessionUserData(userData);
            setSelectedDept(userData?.DeptId);
        }
    }, []);

    // Department dropdown is shown for Organization KPIs, or Department KPIs without a parent
    const showDeptSelect = activeTab === "organization" || !selectedParentId;

    // Single place that builds a KPI row — so refId is never missed
    const buildKPI = () => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        KPIName: "",
        Measurables: "",
        objective: "",
        targetValue: "",
        uom: selectedParentUOMId || null,
        deptId: sessionUserData?.DeptId ?? null,
        // With a parent KPI → parent's dept (e.g. 21); otherwise default to user's dept
        refId: selectedParentId
            ? (selectedMainDept ?? null)
            : (selectedDept ?? sessionUserData?.DeptId ?? null),
        parentId: activeTab === "department" ? selectedParentId : null,
    });

    const [kpis, setKpis] = useState(() => [buildKPI()]);

    const resetKPIs = () => {
        const first = buildKPI();
        setKpis([first]);
        setActiveKeys([first.id]);
    };

    // Fresh row every time the modal opens (or the parent / dept context changes)
    useEffect(() => {
        if (open) resetKPIs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, sessionUserData, selectedDept, selectedMainDept, selectedParentId, selectedParentUOMId, activeTab]);

    const addKPI = () => {
        const next = buildKPI();
        setKpis(prev => [...prev, next]);
        // Close previous panels and open only the new one
        setActiveKeys([next.id]);
    };

    const removeKPI = (id) => {
        const updated = kpis.filter(x => x.id !== id);
        setKpis(updated);

        if (updated.length > 0) {
            setActiveKeys([updated[updated.length - 1].id]);
        }
    };

    const updateKPI = (id, field, value) => {
        setKpis(prev =>
            prev.map(item =>
                item.id === id
                    ? { ...item, [field]: value }
                    : item
            )
        );
    };

    const handleSave = async () => {
        for (let i = 0; i < kpis.length; i++) {
            const item = kpis[i];

            if (activeTab === "department" && !item.parentId && selectedParentId) {
                setActiveKeys([item.id]);
                return message.warning(`Please select Parent KPI for KPI #${i + 1}.`);
            }

            if (!item.KPIName?.trim()) {
                setActiveKeys([item.id]);
                return message.warning(`Please enter KPI Name for KPI #${i + 1}.`);
            }

            if (showDeptSelect && !item.refId) {
                setActiveKeys([item.id]);
                return message.warning(`Please select Department for KPI #${i + 1}.`);
            }

            if (!item.Measurables?.trim()) {
                setActiveKeys([item.id]);
                return message.warning(`Please enter Measurables for KPI #${i + 1}.`);
            }

            if (!item.uom) {
                setActiveKeys([item.id]);
                return message.warning(`Please select Unit Of Measure for KPI #${i + 1}.`);
            }

            if (!item.objective?.replace(/<[^>]*>/g, "").trim()) {
                setActiveKeys([item.id]);
                return message.warning(`Please enter Objective for KPI #${i + 1}.`);
            }
        }

        const success = await onSave(kpis);

        if (success) resetKPIs();
    };

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            width={900}
            title={
                <div className="d-flex justify-content-between align-items-center pe-4">
                    <div className="d-flex align-items-center">
                        <div
                            className="rounded-circle bg-light-primary d-flex align-items-center justify-content-center me-3"
                            style={{ width: "50px", height: "50px" }}
                        >
                            <i className="fa fa-bullseye text-primary fs-3"></i>
                        </div>
                        <div>
                            <h4 className="fw-bold mb-0">{title}</h4>
                            <span className="text-muted fs-7">Configure KPI objectives</span>
                        </div>
                    </div>
                    <div className="badge badge-light-primary px-4 py-2">
                        {kpis.length} KPI{kpis.length > 1 ? "'s" : ""}
                    </div>
                </div>
            }
            footer={[
                <button
                    key="cancel"
                    className="btn btn-light btn-sm me-2"
                    onClick={onCancel}
                    disabled={loading}
                >
                    Cancel
                </button>,
                <button
                    key="save"
                    className="btn btn-primary btn-sm"
                    onClick={handleSave}
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Saving...
                        </>
                    ) : (
                        <>
                            <i className="fa fa-save me-2"></i>
                            Save KPI
                        </>
                    )}
                </button>
            ]}
        >
            <hr className="text-primary" />

            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h6 className="fw-bold mb-1">KPI Configuration</h6>
                </div>
                {activeTab !== "department" && (
                    <button
                        className="btn btn-primary btn-sm shadow-sm"
                        onClick={addKPI}
                    >
                        <i className="fa fa-plus me-2"></i>
                        Add KPI
                    </button>
                )}
            </div>

            <div style={{ maxHeight: "340px", overflowY: "auto", paddingRight: "5px" }}>
                <Collapse
                    activeKey={activeKeys}
                    onChange={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])}
                >
                    {kpis.map((item, index) => (
                        <Collapse.Panel
                            key={item.id}
                            header={
                                <div className="d-flex justify-content-between align-items-center w-100">
                                    <span className="fw-bold">KPI #{index + 1}</span>
                                    {kpis.length > 1 && (
                                        <button
                                            type="button"
                                            className="btn btn-light-danger btn-sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeKPI(item.id);
                                            }}
                                        >
                                            <i className="fa fa-trash me-1"></i>
                                            Remove
                                        </button>
                                    )}
                                </div>
                            }
                        >
                            <div className="row g-4">
                                <div className={showDeptSelect ? "col-12 col-md-8" : "col-12"}>
                                    <label className="form-label">
                                        KPI Name<span className="text-danger">*</span>
                                    </label>
                                    <Input
                                        value={item.KPIName}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            const formattedValue = value.charAt(0).toUpperCase() + value.slice(1);
                                            updateKPI(item.id, "KPIName", formattedValue);
                                        }}
                                        placeholder="Enter KPI Name"
                                    />
                                </div>

                                {showDeptSelect && (
                                    <div className="col-12 col-md-4">
                                        <label className="form-label">
                                            Department<span className="text-danger">*</span>
                                        </label>
                                        <Select
                                            style={{ width: "100%" }}
                                            placeholder="Select Department"
                                            showSearch
                                            optionFilterProp="label"
                                            value={item.refId ?? null}
                                            onChange={(value) => updateKPI(item.id, "refId", value)}
                                            options={(depts || []).map((dept) => ({
                                                value: dept.ItemId,
                                                label: dept.ItemValue,
                                            }))}
                                        />
                                    </div>
                                )}

                                <div className={showDeptSelect ? "col-12 col-md-8" : "col-12"}>
                                    <label className="form-label">
                                        Measurables<span className="text-danger">*</span>
                                    </label>
                                    <Input
                                        value={item.Measurables}
                                        onChange={(e) => updateKPI(item.id, "Measurables", e.target.value)}
                                        placeholder="Enter Measurables"
                                    />
                                </div>

                                {showDeptSelect && (
                                    <div className="col-12 col-md-4">
                                        <label className="form-label">
                                            Unit Of Measure<span className="text-danger">*</span>
                                        </label>
                                        <Select
                                            style={{ width: "100%" }}
                                            placeholder="Select UOM"
                                            value={item.uom}
                                            onChange={(value) => updateKPI(item.id, "uom", value)}
                                            options={(uomTypes || []).map(uom => ({
                                                value: uom.Id,
                                                label: uom.TypeName
                                            }))}
                                        />
                                    </div>
                                )}

                                <div className="col-md-12">
                                    <label className="form-label">
                                        Objectives <span className="text-danger">*</span>
                                    </label>
                                    <ReactQuill
                                        theme="snow"
                                        value={item.objective || ""}
                                        onChange={(value) => updateKPI(item.id, "objective", value)}
                                        placeholder="Enter objectives..."
                                        className="objective-quill"
                                    />
                                </div>
                            </div>
                        </Collapse.Panel>
                    ))}
                </Collapse>
            </div>

            <style>
                {`
                    .objective-quill .ql-editor {
                        min-height: 95px;
                        max-height: 95px;
                        overflow-y: auto;
                    }

                    .objective-quill .ql-container {
                        height: 95px;
                    }
                `}
            </style>
        </Modal>
    );
}